import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useParams, useSearchParams, Link } from 'react-router-dom';
import { api, errorCode, errorDetails, errorMessage } from '../lib/api';
import type { Branding } from '../lib/types';
import { useAuth } from '../store/auth';
import { useApplyBranding } from '../store/branding';
import { Icon } from '../components/Icon';
import { TargetMark } from '../components/TargetMark';

interface OrgChoice {
  slug: string;
  name: string;
}

/**
 * The way in: what this is, set large, and the form beside it — one
 * composition in the middle of a white page.
 *
 * Everyone who reaches this page is either a candidate about to sit a paper —
 * often on a machine they have never used, minutes before the clock starts —
 * or a teacher between lessons. So the type does the work, the centre's colour
 * is kept for the one thing to press, and nothing stands in a box of its own.
 */
export function LoginPage() {
  const params = useParams<{ slug?: string }>();
  const [search] = useSearchParams();
  const location = useLocation();
  const slug = params.slug ?? search.get('org') ?? undefined;

  const signIn = useAuth((s) => s.signIn);

  const [branding, setBranding] = useState<Branding | null>(null);
  /**
   * Whether the centre's colours have arrived — or will not. The band and the
   * button are painted in them, and painting first in the default blue meant
   * a centre's page opened blue and turned red a moment later. Given up on
   * after a second and a half, so a slow connection still gets a page.
   */
  const [ready, setReady] = useState(!slug);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [choices, setChoices] = useState<OrgChoice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** The password shown as typed, for checking a temporary one letter by letter. */
  const [reveal, setReveal] = useState(false);

  useApplyBranding(branding);

  /** Sent here from a page that needed signing in for, rather than arriving. */
  const redirected = Boolean((location.state as { from?: unknown } | null)?.from);

  // The login page is the one screen that has to paint a tenant's identity
  // before anyone is authenticated, which is why branding has a public endpoint.
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    const giveUp = window.setTimeout(() => setReady(true), 1_500);
    api
      .get<Branding>(`/organizations/public/${slug}`)
      .then((res) => !cancelled && setBranding(res.data))
      .catch(() => undefined)
      .finally(() => !cancelled && setReady(true));
    return () => {
      cancelled = true;
      window.clearTimeout(giveUp);
    };
  }, [slug]);

  // The field takes the cursor once the page is shown: a hidden field cannot
  // be focused, so autoFocus alone found nothing to focus on a centre's page.
  useEffect(() => {
    if (!ready) return;
    const field = document.getElementById('signin-id');
    if (field && document.activeElement === document.body) field.focus();
  }, [ready]);

  async function submit(e: FormEvent, organizationSlug?: string) {
    e.preventDefault();
    // Masked again before it is sent, so a password manager offering to save
    // it sees a password field rather than a text box.
    setReveal(false);
    setBusy(true);
    setError(null);
    try {
      await signIn(identifier, password, organizationSlug ?? slug);
    } catch (err) {
      // One number can be enrolled at more than one centre. The API confirms
      // the password first and only then offers the accounts it opens.
      if (errorCode(err) === 'ORG_REQUIRED') {
        const details = errorDetails<{ organizations: OrgChoice[] }>(err);
        setChoices(details?.organizations ?? []);
      } else {
        setError(errorMessage(err, 'Could not sign in'));
      }
    } finally {
      setBusy(false);
    }
  }

  const name = branding?.name ?? '';

  return (
    <div className="signin" data-ready={ready || undefined}>
      <header className="signin__bar">
        {/* The centre's logo or name on a centre's own page. Target's mark
            where no centre is named, and on the platform's own workspace, whose
            name labels a shelf of papers rather than a centre. */}
        <div className="signin__mark">
          {branding?.logoUrl ? (
            <img src={branding.logoUrl} alt={name} />
          ) : name && !branding?.isPlatform ? (
            <span className="signin__name">{name}</span>
          ) : (
            <Link to="/" className="signin__target" aria-label="Target">
              <TargetMark size={22} />
              Target
            </Link>
          )}
        </div>
        <Clock />
      </header>

      <main className="signin__main">
        {/* What this is, said once and large. Type only: the page is the
            door to a test, not an advertisement for one. */}
        <section className="signin__intro" aria-label="About this platform">
          <p className="signin__claim">
            Get ready for the
            <br />
            <span>computer exam.</span>
          </p>
          <p className="signin__lede">
            Practise on the same screen you will see on test day, with the same timing and tools.
          </p>
          <ul className="signin__facts">
            <li>Listening, Reading and Writing</li>
            <li>Timed like the real test</li>
            <li>Your answers save automatically</li>
          </ul>
        </section>

        <div className="signin__panel">

          {choices ? (
            <>
              <h1 className="signin__welcome">Choose your centre</h1>
              <p className="signin__sub">
                That number is enrolled at more than one. Choose the one you are sitting with.
              </p>
              <div className="signin__choices">
                {choices.map((choice) => (
                  <button
                    key={choice.slug}
                    className="signin__choice"
                    onClick={(e) => void submit(e, choice.slug)}
                    disabled={busy}
                  >
                    {choice.name}
                    <Icon name="arrowRight" size={18} />
                  </button>
                ))}
              </div>
              <button className="signin__back" onClick={() => setChoices(null)}>
                Back
              </button>
            </>
          ) : (
            <>
              <h1 className="signin__welcome">Sign in</h1>
              <p className="signin__sub">Use the number and password from your teacher.</p>

              {/* Only when they were sent here. Arriving at the login page and
                  being told to log in is not news. */}
              {redirected && !error && (
                <p className="signin__notice">
                  <Icon name="lock" className="signin__noticeIcon" />
                  <span>That page needs you signed in. Sign in below and you will be taken straight to it.</span>
                </p>
              )}
              {/*
                * Announced, not only shown. A candidate who cannot see the page
                * gets the same news as one who can: the sign-in failed and why.
                */}
              {error && (
                <p className="signin__notice signin__notice--error" role="alert">
                  <Icon name="x" className="signin__noticeIcon" />
                  <span>{error}</span>
                </p>
              )}

              <form className="signin__form" onSubmit={(e) => void submit(e)}>
                {/* Labels that stay put. A hint inside the field disappears
                    at the first keystroke, and with it what the field was. */}
                <div>
                  <label className="signin__label" htmlFor="signin-id">
                    Sign-in number
                  </label>
                  <input
                    id="signin-id"
                    className="signin__field"
                    type="tel"
                    inputMode="tel"
                    autoComplete="username"
                    autoFocus
                    required
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                  />
                </div>

                <div>
                  <label className="signin__label" htmlFor="signin-password">
                    Password
                  </label>
                  <div className="signin__secret">
                    <input
                      id="signin-password"
                      className="signin__field"
                      type={reveal ? 'text' : 'password'}
                      autoComplete="current-password"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      className="signin__reveal"
                      aria-controls="signin-password"
                      aria-label={reveal ? 'Hide password' : 'Show password'}
                      onClick={() => setReveal((v) => !v)}
                    >
                      {reveal ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>

                <button className="signin__submit" type="submit" disabled={busy}>
                  {/* Keyed, so the new words arrive as a new label: the
                      stylesheet brings each one in through a slight blur. */}
                  <span key={busy ? 'busy' : 'idle'}>{busy ? 'Signing in…' : 'Log in'}</span>
                  {!busy && <Icon name="arrowRight" size={18} className="signin__go" />}
                </button>

                {/* Terms nobody was shown are terms nobody agreed to. */}
                <p className="signin__terms">
                  By logging in you accept the <Link to="/legal#terms">Terms of use</Link>, and your
                  information is handled as the <Link to="/legal#privacy">Privacy notice</Link>{' '}
                  describes.
                </p>
              </form>
            </>
          )}

        <p className="signin__help">
          Trouble logging in?{' '}
          {branding?.supportEmail ? (
            <>
              Contact <a href={`mailto:${branding.supportEmail}`}>{branding.supportEmail}</a>.
            </>
          ) : (
            'Ask your teacher for the number and password you were enrolled with.'
          )}
        </p>
        </div>
      </main>

      <footer className="signin__foot">
        <p>
          <Link to="/legal">About this platform</Link>. Independent practice, not affiliated with
          any examination board; no qualification is awarded.
        </p>
        {/* Only on the platform's own sign-in. A centre's page is the centre's,
            and is not the place to offer Target to its competitors. */}
        {!slug && (
          <p className="signin__forcentres">
            Run a language centre?{' '}
            <Link to="/centres">
              Target for centres <Icon name="arrowRight" size={13} />
            </Link>
          </p>
        )}
      </footer>
    </div>
  );
}

const DAY = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
const TIME = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });

/**
 * The day and the time, as an exam machine shows them across the top.
 *
 * A candidate at this screen is usually minutes from a start time, on a
 * computer whose own clock may be hidden behind the browser. Checked every
 * fifteen seconds, so the minute turns over close to when it does.
 */
function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 15_000);
    return () => window.clearInterval(timer);
  }, []);
  return (
    <p className="signin__clock">
      <time dateTime={now.toISOString()}>
        {/* One line of text with a real space in it, so it is read as
            "Tuesday 29 September 17:31"; the rule between the two is drawn
            by the stylesheet and read by nobody. */}
        <span className="signin__date">{DAY.format(now)}</span> <b>{TIME.format(now)}</b>
      </time>
    </p>
  );
}
